import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import type { components } from '../../api/tipos'

export type Canal = components['schemas']['Canal']

/**
 * `meta` es un diccionario abierto en el backend (`dict[str, Any]`), y de eso
 * openapi-typescript saca `Record<string, never>`: un tipo que dice "objeto sin
 * ninguna propiedad util". Es un artefacto del generador, no el contrato. Se
 * corrige aqui, en un solo sitio, en vez de repartir `as` por los componentes.
 */
export type Metadatos = Record<string, unknown>

type SesionGenerada = components['schemas']['SesionResumen']
type MensajeGenerado = components['schemas']['Mensaje']
type PaginaGenerada = components['schemas']['Pagina_SesionResumen_']

export type SesionResumen = Omit<SesionGenerada, 'meta'> & { meta?: Metadatos | null; nombre?: string | null }
export type Mensaje = Omit<MensajeGenerado, 'meta'> & { meta?: Metadatos | null }
export type PaginaSesiones = Omit<PaginaGenerada, 'items'> & { items: SesionResumen[] }

/** El backend no limita el page_size; clientes ~243 sesiones. Traemos todas de
 *  una (son pocas) para que la búsqueda local encuentre también las antiguas,
 *  que de otro modo caerían fuera de la primera página y nunca se cargarían. */
export const TAMANO_PAGINA = 500

/** El sondeo de la conversacion abierta. */
export const INTERVALO_SONDEO = 12_000

/**
 * Codifica un identificador de sesion para meterlo en un segmento de ruta.
 *
 * En clientes el identificador es el nombre de perfil de WhatsApp: `Angie`,
 * `Administración Obras Maestrat`, `🧔🏻`, `❤️`, y **un unico punto**.
 * `encodeURIComponent` deja el punto sin tocar, y un segmento que sea
 * exactamente `.` o `..` lo colapsa la norma de resolucion de rutas antes de
 * que la peticion salga del cliente. Por eso el punto se escapa tambien, tal y
 * como documenta el parametro en `/docs`.
 *
 * ⚠️ Medido: el analizador de URL de los navegadores (y el de `fetch` en Node)
 * trata `%2E` a solas como un segmento de un punto y lo colapsa igualmente, asi
 * que la sesion llamada exactamente `.` no es alcanzable por ruta desde un
 * navegador, escapada o no. La codificacion correcta es esta; el hueco esta en
 * el contrato del endpoint, que necesitaria aceptar el identificador fuera de
 * la ruta. Cualquier otro nombre, punto incluido (`Ana.M`), viaja bien.
 */
export function codificarSesion(sid: string): string {
  return encodeURIComponent(sid).replace(/\./g, '%2E')
}

function rutaSesiones(canal: Canal): string {
  return `/api/conversaciones/${canal}/sesiones`
}

export function useSesiones(canal: Canal, page: number) {
  const api = useApi()

  return useQuery<PaginaSesiones, ErrorApi>({
    queryKey: ['sesiones', canal, page],
    queryFn: ({ signal }) =>
      api.pedir<PaginaSesiones>(rutaSesiones(canal), {
        parametros: { page, page_size: TAMANO_PAGINA },
        senal: signal,
      }),
  })
}

/**
 * Une lo que ya se tenia con lo que ha traido el sondeo. Devuelve el array
 * anterior sin tocar cuando no hay novedades: asi la referencia no cambia,
 * React no vuelve a pintar 250 burbujas cada 12 segundos y la posicion del
 * scroll se queda donde el operador la dejo.
 */
function unir(previos: Mensaje[], nuevos: Mensaje[]): Mensaje[] {
  if (nuevos.length === 0) return previos
  const conocidos = new Set(previos.map((mensaje) => mensaje.id))
  const extra = nuevos.filter((mensaje) => !conocidos.has(mensaje.id))
  return extra.length === 0 ? previos : [...previos, ...extra]
}

/**
 * Los mensajes de una conversacion, con sondeo incremental.
 *
 * Solo sondea la conversacion abierta (`enabled`), y solo con la pestana
 * delante (`refetchIntervalInBackground: false`). Cada vuelta manda el `id` del
 * ultimo mensaje conocido como cursor `desde`, de modo que la conversacion de
 * 250 mensajes se descarga una vez y a partir de ahi solo viaja lo nuevo.
 */
export function useMensajes(canal: Canal, sesion: string | null) {
  const api = useApi()
  const consultas = useQueryClient()
  const clave = ['mensajes', canal, sesion] as const

  return useQuery<Mensaje[], ErrorApi>({
    queryKey: clave,
    enabled: sesion !== null,
    queryFn: async ({ signal }) => {
      // No puede ocurrir con `enabled`, pero el tipo no lo sabe y una guarda
      // honesta es mejor que una asercion.
      if (sesion === null) return []

      const previos = consultas.getQueryData<Mensaje[]>(clave) ?? []
      const desde = previos.at(-1)?.id
      const ruta = `${rutaSesiones(canal)}/${codificarSesion(sesion)}/mensajes`
      const nuevos = await api.pedir<Mensaje[]>(ruta, {
        parametros: { desde },
        senal: signal,
      })

      return desde === undefined ? nuevos : unir(previos, nuevos)
    },
    refetchInterval: INTERVALO_SONDEO,
    refetchIntervalInBackground: false,
    // El sondeo ya es la fuente de frescura: sin esto, volver a la pestana
    // dispararia ademas una recarga completa de la conversacion.
    staleTime: INTERVALO_SONDEO,
  })
}

// ---- Envío manual desde el panel + ventana 24h + pausa del bot ------------

/** Estado de la ventana de 24h de Meta para una sesión. */
export interface VentanaEstado {
  abierta: boolean
  ultimo_entrante: string | null
  expira_en: string | null
}

/** Estado del bot (pausado o no) para un contacto. */
export interface BotEstado {
  pausado: boolean
  pausadoHasta?: string | null
  operador?: string | null
}

/** Resultado de intentar enviar un mensaje. */
export interface ResultadoEnvio {
  ok: boolean
  estado: string
  wamid?: string
  motivo?: string
  bot?: BotEstado
}

/** Sondea el estado de la ventana de 24h de la sesión abierta (chip verde/ámbar). */
export function useVentana(canal: Canal, sesion: string | null) {
  const api = useApi()
  return useQuery<VentanaEstado, ErrorApi>({
    queryKey: ['ventana', canal, sesion],
    enabled: sesion !== null,
    queryFn: ({ signal }) => {
      if (sesion === null) return Promise.resolve({ abierta: false, ultimo_entrante: null, expira_en: null })
      const ruta = `${rutaSesiones(canal)}/${codificarSesion(sesion)}/ventana`
      return api.pedir<VentanaEstado>(ruta, { senal: signal })
    },
    refetchInterval: INTERVALO_SONDEO,
    refetchIntervalInBackground: false,
  })
}

/** Estado del bot (pausado/activo) para un contacto — línea bajo el hilo. */
export function useBot(canal: Canal, telefono: string | null) {
  const api = useApi()
  return useQuery<BotEstado, ErrorApi>({
    queryKey: ['bot', canal, telefono],
    enabled: !!telefono,
    queryFn: ({ signal }) => {
      if (!telefono) return Promise.resolve({ pausado: false })
      const ruta = `/api/conversaciones/${canal}/bot/${encodeURIComponent(telefono)}`
      return api.pedir<BotEstado>(ruta, { senal: signal })
    },
    refetchInterval: INTERVALO_SONDEO,
    refetchIntervalInBackground: false,
  })
}

/** Envía un texto libre; al terminar refresca mensajes, ventana y estado del bot. */
export function useEnviarMensaje(canal: Canal, sesion: string | null, telefono: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<ResultadoEnvio, ErrorApi, { texto: string; operador?: string }>({
    mutationFn: ({ texto, operador }) => {
      const ruta = `${rutaSesiones(canal)}/${codificarSesion(sesion ?? '')}/enviar`
      return api.pedir<ResultadoEnvio>(ruta, {
        metodo: 'POST',
        cuerpo: { telefono: telefono ?? '', texto, operador },
      })
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['mensajes', canal, sesion] })
      void qc.invalidateQueries({ queryKey: ['ventana', canal, sesion] })
      if (res.bot) qc.setQueryData(['bot', canal, telefono], res.bot)
      else void qc.invalidateQueries({ queryKey: ['bot', canal, telefono] })
    },
  })
}

/** Reactiva el bot para un contacto (devolver la conversación al bot). */
export function useReanudarBot(canal: Canal, telefono: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<BotEstado, ErrorApi, void>({
    mutationFn: () =>
      api.pedir<BotEstado>(`/api/conversaciones/${canal}/bot/reanudar`, {
        metodo: 'POST',
        cuerpo: { telefono: telefono ?? '' },
      }),
    onSuccess: () => {
      qc.setQueryData(['bot', canal, telefono], { pausado: false })
    },
  })
}
